export type DialScope = "all" | "style" | string;
export type DialBank = "frequency" | "instruments" | "vocals" | "style" | "mimic";
export type EqBandId = "hpf" | "sub" | "bass" | "lowmid" | "presence" | "air" | "lpf";

export type InstrumentFilters = Record<EqBandId, number>;

export const EQ_GAIN_BANDS: { id: Exclude<EqBandId, "hpf" | "lpf">; label: string; hz: number }[] = [
  { id: "sub", label: "Sub", hz: 60 },
  { id: "bass", label: "Bass", hz: 110 },
  { id: "lowmid", label: "Low-mid", hz: 400 },
  { id: "presence", label: "Presence", hz: 3000 },
  { id: "air", label: "Air", hz: 12000 },
];

export function eqGainDb(value: number): number {
  return Math.round(((Math.max(0, Math.min(100, value)) - 50) / 50) * 12);
}

export function formatHz(hz: number): string {
  return hz >= 1000 ? `${Math.round(hz / 100) / 10} kHz` : `${hz} Hz`;
}

export function flatFilters(): InstrumentFilters {
  return { hpf: 0, sub: 50, bass: 50, lowmid: 50, presence: 50, air: 50, lpf: 0 };
}

export function filtersTouched(f?: InstrumentFilters | null): boolean {
  if (!f) return false;
  if (f.hpf > 4 || f.lpf > 4) return true;
  return EQ_GAIN_BANDS.some((b) => Math.abs((f[b.id] ?? 50) - 50) > 4);
}

export type Dial = {
  id: string;
  label: string;
  value: number;
  scope: DialScope;
  bank: DialBank;
  custom?: boolean;
  /** Per-instrument (or style) EQ. Master track EQ lives on frequency dials with scope "all". */
  filters?: InstrumentFilters;
};

export const MIX_BANKS: { id: DialBank | "seed"; label: string }[] = [
  { id: "frequency", label: "Frequency" },
  { id: "instruments", label: "Instruments" },
  { id: "vocals", label: "Vocals" },
  { id: "style", label: "Style" },
  { id: "mimic", label: "Mimic" },
  { id: "seed", label: "Seed" },
];

export const BPM_PRESETS = [50, 60, 70, 80, 90, 100, 110, 120, 128, 140, 174] as const;

export const GENRE_CHIPS = [
  "healing",
  "meditation",
  "ambient",
  "drone",
  "pop",
  "rock",
  "hip-hop",
  "r&b",
  "soul",
  "edm",
  "house",
  "dubstep",
  "drum & bass",
  "trap",
  "lo-fi",
  "folk",
  "jazz",
  "blues",
  "metal",
  "country",
  "cinematic",
  "synthwave",
] as const;

export const DEFAULT_DIALS: Dial[] = [
  { id: "hpf", label: "High-pass", value: 0, scope: "all", bank: "frequency" },
  { id: "sub", label: "Sub", value: 45, scope: "all", bank: "frequency" },
  { id: "bass", label: "Bass", value: 52, scope: "all", bank: "frequency" },
  { id: "lowmid", label: "Low-mid", value: 48, scope: "all", bank: "frequency" },
  { id: "presence", label: "Presence", value: 55, scope: "all", bank: "frequency" },
  { id: "air", label: "Air / brilliance", value: 50, scope: "all", bank: "frequency" },
  { id: "lpf", label: "Low-pass", value: 0, scope: "all", bank: "frequency" },
  {
    id: "styleeq",
    label: "Style EQ",
    value: 50,
    scope: "style",
    bank: "frequency",
    filters: flatFilters(),
  },
  { id: "kick", label: "Kick", value: 55, scope: "all", bank: "instruments" },
  { id: "snare", label: "Snare / clap", value: 50, scope: "all", bank: "instruments" },
  { id: "hats", label: "Hats", value: 45, scope: "all", bank: "instruments" },
  { id: "bassinst", label: "Bass instrument", value: 50, scope: "all", bank: "instruments" },
  { id: "keys", label: "Piano / keys", value: 40, scope: "all", bank: "instruments" },
  { id: "guitar", label: "Guitar", value: 30, scope: "all", bank: "instruments" },
  { id: "synth", label: "Synth lead", value: 35, scope: "all", bank: "instruments" },
  { id: "pads", label: "Pads", value: 40, scope: "all", bank: "instruments" },
  { id: "strings", label: "Strings", value: 20, scope: "all", bank: "instruments" },
  { id: "fx", label: "FX / risers", value: 25, scope: "all", bank: "instruments" },
  { id: "vocal", label: "Lead vocal", value: 70, scope: "all", bank: "vocals" },
  { id: "doubles", label: "Doubles / unisons", value: 35, scope: "all", bank: "vocals" },
  { id: "harmony", label: "Harmony stack", value: 40, scope: "all", bank: "vocals" },
  { id: "adlibs", label: "Ad-libs", value: 25, scope: "all", bank: "vocals" },
  { id: "breath", label: "Breath / intimacy", value: 45, scope: "all", bank: "vocals" },
  { id: "grit", label: "Grit / rasp", value: 20, scope: "all", bank: "vocals" },
  { id: "vibrato", label: "Vibrato", value: 40, scope: "all", bank: "vocals" },
  {
    id: "vocaleq",
    label: "Lead vocal EQ",
    value: 50,
    scope: "vocals",
    bank: "vocals",
    filters: flatFilters(),
  },
  { id: "energy", label: "Energy", value: 55, scope: "all", bank: "style" },
  { id: "groove", label: "Groove / drum punch", value: 50, scope: "all", bank: "style" },
  { id: "vintage", label: "Vintage / tape", value: 25, scope: "all", bank: "style" },
  { id: "space", label: "Space / reverb", value: 40, scope: "all", bank: "style" },
  { id: "dark", label: "Darkness", value: 35, scope: "all", bank: "style" },
  { id: "punch", label: "Punch / presence", value: 55, scope: "all", bank: "style" },
  { id: "polish", label: "Mix polish", value: 55, scope: "all", bank: "style" },
  { id: "mimicwt", label: "Inspired-by weight", value: 0, scope: "all", bank: "mimic" },
];

export function intensityBand(value: number): string {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  if (v === 0) return "omit";
  if (v <= 30) return "whisper";
  if (v <= 60) return "present";
  if (v <= 85) return "prominent";
  return "featured";
}

export function intensityPhrase(label: string, value: number): string | null {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const name = label.trim() || "this color";
  if (v === 0) return `none — do not use ${name}`;
  if (v <= 30) return `a whisper of ${name} (${v}/100)`;
  if (v <= 60) return `present ${name} (${v}/100)`;
  if (v <= 85) return `prominent ${name} (${v}/100)`;
  return `featured / dominant ${name} (${v}/100)`;
}

export function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export const HPF_PRESETS = [0, 40, 80, 120, 200] as const;
export const LPF_PRESETS = [20000, 12000, 8000, 5000] as const;

export function hpfHz(value: number): number {
  return Math.round(20 + (Math.max(0, Math.min(100, value)) / 100) * 380);
}

export function lpfHz(value: number): number {
  const t = Math.max(0, Math.min(100, value)) / 100;
  return Math.round(20000 - t * 16000);
}

export function valueFromHpfHz(hz: number): number {
  if (hz <= 20) return 0;
  return Math.max(0, Math.min(100, Math.round(((hz - 20) / 380) * 100)));
}

export function valueFromLpfHz(hz: number): number {
  if (hz >= 20000) return 0;
  return Math.max(0, Math.min(100, Math.round(((20000 - hz) / 16000) * 100)));
}

export function withBank(d: Dial, fallback: DialBank = "instruments"): Dial {
  return { ...d, bank: d.bank || fallback };
}

export function foldInstrumentName(s: string): string {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function masterFilters(dials: Dial[]): InstrumentFilters {
  const pick = (id: EqBandId, fallback: number) =>
    dials.find((d) => d.id === id && d.bank === "frequency" && (d.scope || "all") === "all")?.value ??
    fallback;
  return {
    hpf: pick("hpf", 0),
    sub: pick("sub", 50),
    bass: pick("bass", 50),
    lowmid: pick("lowmid", 50),
    presence: pick("presence", 50),
    air: pick("air", 50),
    lpf: pick("lpf", 0),
  };
}

export function applyMasterFilters(dials: Dial[], f: InstrumentFilters): Dial[] {
  return dials.map((d) => {
    if (d.bank !== "frequency" || (d.scope || "all") !== "all") return d;
    if (d.id === "hpf" || d.id === "sub" || d.id === "bass" || d.id === "lowmid" || d.id === "presence" || d.id === "air" || d.id === "lpf") {
      return { ...d, value: f[d.id] };
    }
    return d;
  });
}

export function styleEqDial(dials: Dial[]): Dial | undefined {
  return dials.find((d) => d.id === "styleeq");
}

export function vocalEqDial(dials: Dial[]): Dial | undefined {
  return dials.find((d) => d.id === "vocaleq");
}

export function instrumentDials(dials: Dial[]): Dial[] {
  return dials.filter((d) => d.bank === "instruments");
}

export function customInstrumentDials(dials: Dial[]): Dial[] {
  return dials.filter((d) => d.bank === "instruments" && d.custom);
}

export function customVocalDials(dials: Dial[]): Dial[] {
  return dials.filter((d) => d.bank === "vocals" && d.custom);
}
